import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { useNavigation } from '@/hooks/useNavigation'
import {
  useModalContext,
  ModalSettings,
} from '@/components/Modal/contexts/ModalContext'
import { useAttendanceContext } from '@/modules/Attendance/contexts/AttendanceContex'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import {
  ATTENDANCE_STATES,
  END_CONSULTATION_STATES,
  SCHEDULING_STATES,
} from '@/modules/RealtimeAttendance/constants'
import { notify } from '@/components/BrowserNotification'
import { capitalize, returnFormattedDate } from '@/utils/fnUtils'
import { getCache } from '@/utils/cache'
import { DateTime } from 'luxon'

import { verifyTokenStatus } from '@/modules/RealtimeAttendance/services'
import { toast } from '@conexasaude/conexa-hero'
import {
  CallData,
  IAttendanceConfig,
  ICall,
  IHandleAttendanceStartWithToken,
} from './types'
import { useMedicalSpecialitiesContext } from '../MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { AUTHENTICATED_2FA_TIME } from '../Auth/pages/TwoFactorAuth/constants'
import { LOGGED_IN_WITH_MAGIC_LINK } from '../Auth/pages/MagicLink/consts'
import { useGetImmediateAttendance } from './queries'
import { useAttendanceCallStore } from './store/useAttendanceCall'
import { useFinishMeet } from '@conexasaude/vonage-sdk'
import { APPOINTMENT_STATES_ENUM } from '../RealtimeAttendanceMonitoring/types'
import { roomPaths } from '../Attendance/submodules/Room/constants'

const useRealtimeAttendance = (): void => {
  const {
    realtimeAttendance: { immediate, nextScheduled, call, isFitInAttendance },
    startTimer,
    clearTimer,
  } = useRealtimeAttendanceContext()
  const navigate = useNavigation()
  const location = useLocation()
  const { showModal, closeModal } = useModalContext()
  const { clinic } = useAuthContext()
  const { resetAttendance } = useAttendanceContext()
  const { withoutInteractionAttendance, embed } = useThemeGeneratorContext()
  const { specialities } = useMedicalSpecialitiesContext()
  const { data: immediateAttendance, refetch } = useGetImmediateAttendance(
    immediate?.status,
  )
  const setAttendanceCall = useAttendanceCallStore(
    store => store.setAttendanceCall,
  )
  const attendanceCall = useAttendanceCallStore(store => store.attendanceCall)
  const [attendanceId, setAttendanceId] = useState<number>(null)
  const { destroyMeet } = useFinishMeet()

  const waitingRoomMatch = useRouteMatch([
    '/atendimento-agendado/sala-de-espera/:id',
    '/atendimento-imediato/waiting',
    roomPaths.immediate.fullPath,
    roomPaths.scheduled.fullPath,
  ])
  const embedOrEmbedFull = embed?.value || embed?.full
  const immediateSpeciality = useMemo(() => {
    return specialities.find(
      s => s.id === immediateAttendance?.professionalTypeId,
    )
  }, [immediateAttendance?.professionalTypeId, specialities])

  const buildCallFromFirebase = useCallback(
    (firebaseCall: ICall) => {
      if (!firebaseCall?.callId) return
      const callObject: CallData = {
        callId: firebaseCall?.callId,
        attendanceId: firebaseCall?.attendanceId,
        professionalId: firebaseCall?.professionalId,
        professionalName: firebaseCall?.professionalName,
        name: firebaseCall?.professionalName,
        urlPhoto: firebaseCall?.urlPhoto,
        specialityId: firebaseCall?.specialityId,
        specialityName: firebaseCall?.speciality,
        zoomMeetingId: firebaseCall?.zoomMeetingId,
        zoomMeetingUrl: firebaseCall?.zoomMeetingUrl,
        zoomMeetingUrlPatient: firebaseCall?.zoomMeetingUrl,
        zoomMeetingPasswd: firebaseCall?.zoomMeetingPass,
        attendanceToken: firebaseCall?.attendanceToken,
        callToken: firebaseCall?.callToken,
        meetingType: firebaseCall?.meetingType,
        scheduleStatus: firebaseCall?.scheduledStatus,
        professionalRegister: null,
        unifiedAttendance: null,
        attendanceMode: firebaseCall?.meetingType,
        queueStatus: firebaseCall?.queueStatus,
        sex: firebaseCall?.sex,
      }
      setAttendanceCall(callObject)
    },
    [setAttendanceCall],
  )

  const scheduledSpeciality = useMemo(() => {
    return specialities.find(
      s => s.id === immediateAttendance?.professionalTypeId,
    )
  }, [immediateAttendance?.professionalTypeId, specialities])

  const canRedirect = useCallback(() => {
    if (location.pathname === '/trocar-senha') return false
    return true
  }, [location])

  const callStartedOptions = [
    ATTENDANCE_STATES.CALL_STARTED,
    ATTENDANCE_STATES.CALL_RESTARTED,
  ]

  const handleAttendanceStartWithToken = async (
    params: IHandleAttendanceStartWithToken,
  ): Promise<void> => {
    const { attendanceId, modalToRender, modalTitle } = params

    try {
      const tokenIsValidated = await verifyTokenStatus(Number(attendanceId))
      if (tokenIsValidated) {
        showModal(modalToRender, {
          title: modalTitle,
          lockable: true,
          persistent: true,
        })
      } else {
        showModal('CALL_STARTED_WITH_TOKEN', {
          title: 'Informe o token de segurança',
          persistent: true,
        })
      }
    } catch (err) {
      navigate('/')
      toast({
        message: err?.response?.data?.msg,
        variant: 'error',
        autoClose: false,
        position: 'top-right',
      })
    }
  }

  const showModalValidating2FA = (id: string, modalSettings: ModalSettings) => {
    const needTwoFactorAuth =
      clinic?.flag?.HabilitarAutenticacaoDoisFatoresLoginPaciente &&
      !getCache(AUTHENTICATED_2FA_TIME) &&
      !getCache(LOGGED_IN_WITH_MAGIC_LINK)

    if (!needTwoFactorAuth) showModal(id, modalSettings)
  }

  const notifyValidating2FA = (
    title: string,
    content: string,
    img?: string,
  ) => {
    const needTwoFactorAuth =
      clinic?.flag?.HabilitarAutenticacaoDoisFatoresLoginPaciente &&
      !getCache(AUTHENTICATED_2FA_TIME) &&
      !getCache(LOGGED_IN_WITH_MAGIC_LINK)

    if (!needTwoFactorAuth) notify(title, content, img)
  }

  useEffect(() => {
    if (
      immediate?.status &&
      immediate?.status !== APPOINTMENT_STATES_ENUM.CALL_ENDED
    ) {
      setAttendanceId(immediate?.attendanceId)
      refetch()
    }
    if (nextScheduled?.status && call) {
      setAttendanceId(nextScheduled?.attendanceId)
      buildCallFromFirebase(call)
    }
    if (!immediateAttendance?.call && !call) {
      setAttendanceCall(null)
    }
  }, [
    immediate,
    nextScheduled,
    call,
    immediateAttendance,
    refetch,
    buildCallFromFirebase,
    setAttendanceCall,
  ])

  const handleRedirectToWaitingRoom = useCallback(async () => {
    if (isFitInAttendance) {
      navigate(
        `/atendimento-agendado/especialidade/encaixe/${immediate?.council}/${immediate?.professionalSpecialtyId}`,
      )
    } else {
      if (clinic?.flag?.NovaSalaDeEspera) {
        const { data: currentAppointment } = await refetch()

        if (
          !currentAppointment?.roomId ||
          currentAppointment?.roomId.trim() === ''
        ) {
          navigate('/atendimento-imediato/waiting')
          return
        }

        const attId = currentAppointment?.attendanceId

        const url = attId
          ? `${roomPaths.immediate.basePath}/${currentAppointment?.roomId}?a=${attId}`
          : `${roomPaths.immediate.basePath}/${currentAppointment?.roomId}`

        navigate(url)

        return
      }

      navigate('/atendimento-imediato/waiting')
    }
  }, [
    clinic?.flag?.NovaSalaDeEspera,
    immediate?.council,
    immediate?.professionalSpecialtyId,
    isFitInAttendance,
    navigate,
    refetch,
  ])

  useEffect(() => {
    if (!canRedirect()) return

    switch (immediate?.status) {
      case ATTENDANCE_STATES.WAITING_ATTENDANCE:
        handleRedirectToWaitingRoom()
        break
      case ATTENDANCE_STATES.ATTENDANCE_STARTED:
        handleRedirectToWaitingRoom()
        notify(
          'Só um minuto',
          `${capitalize(
            immediateAttendance?.professionalName?.trim()?.toLowerCase(),
          )} já está analisando suas informações e em breve iniciará o atendimento.`,
          immediateAttendance?.professionalPhoto,
        )
        break
      case ATTENDANCE_STATES.ATTENDANCE_END:
        resetAttendance()
        closeModal()

        if (clinic?.flag?.DesabilitarNpsTelemedicina) {
          embedOrEmbedFull ? navigate('/meus-atendimentos') : navigate('/')
          return
        }
        if (immediateAttendance?.attendanceId || attendanceId)
          navigate(
            `/avaliacao/${immediateAttendance?.attendanceId || attendanceId}`,
            {
              consultationType: 'immediate',
            },
          )

        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_MEDIC:
        resetAttendance()
        clearTimer()
        if (waitingRoomMatch?.isExact) {
          showModal('CANCELLED_BY_MEDIC', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
            width: 512,
          })
        }
        break
      case ATTENDANCE_STATES.CANCELLED_DUE_TO_AUTHORIZATION_FAILURE:
        resetAttendance()
        clearTimer()
        if (waitingRoomMatch?.isExact) {
          showModal('CANCELLED_DUE_TO_AUTHORIZATION_FAILURE', {
            title: 'Não foi possível validar sua consulta',
            lockable: true,
            persistent: true,
            width: 512,
          })
        }
        break
      case ATTENDANCE_STATES.UNAUTHORIZED_SERVICE:
        resetAttendance()
        clearTimer()
        if (waitingRoomMatch?.isExact) {
          showModal('CANCELLED_UNAUTHORIZED_SERVICE', {
            title: 'Não foi possível validar sua consulta',
            lockable: true,
            persistent: true,
            width: 512,
          })
        }
        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_TIME:
        resetAttendance()
        clearTimer()

        if (immediate?.fitInAttendance) {
          showModal('TIME_SLOT_CANCELED_MODAL', {
            title: 'Consulta cancelada',
            width: 480,
          })

          return
        }

        if (immediateSpeciality?.canSchedule && !embedOrEmbedFull) {
          showModal('CANCELLED_BY_EXCEEDED_TIME_WITH_SCHEDULE', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
            width: 512,
          })
        } else {
          showModal('CANCELLED_BY_EXCEEDED_TIME', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
            width: 512,
          })
        }

        if (
          location.pathname === '/chamada' ||
          location?.pathname?.includes(roomPaths.basePath)
        ) {
          showModal('CANCELLED_BY_VIDEO_ERROR', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
            width: 512,
          })
        }
        break
      case ATTENDANCE_STATES.CALL_STARTED:
        if (
          location.pathname === '/chamada' ||
          location?.pathname?.includes(roomPaths.basePath)
        )
          return
        if (withoutInteractionAttendance?.value) {
          navigate('/chamada')
          return
        }
        notify(
          'Chegou a sua vez!',
          `${capitalize(
            immediateAttendance?.professionalName?.trim()?.toLowerCase(),
          )} já está aguardando por você. Vamos começar?`,
          immediateAttendance?.professionalPhoto,
        )

        if (
          clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno &&
          !clinic?.flag?.NovaSalaDeEspera
        ) {
          const attendanceConfig: IAttendanceConfig = {
            attendanceId:
              immediateAttendance?.call?.attendanceId ?? attendanceId,
            modalToRender: 'CALL_STARTED',
            modalTitle: 'Chegou a sua vez!',
          }
          handleAttendanceStartWithToken(attendanceConfig)
        } else {
          setTimeout(() => {
            showModal('CALL_STARTED', {
              title: 'Chegou a sua vez!',
              lockable: true,
              persistent: true,
            })
          }, 500)
        }

        startTimer()
        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_PATIENT_BLOCKED:
        resetAttendance()
        showModal('CANCELLED_BY_CLINIC', {
          title: 'Acesso bloqueado para realizar consultas',
          lockable: true,
          persistent: true,
          width: 512,
        })
        clearTimer()
        break
      case ATTENDANCE_STATES.CALL_RESTARTED:
        if (
          location.pathname === '/chamada' ||
          location?.pathname?.includes(roomPaths.basePath)
        )
          return

        notify(
          'Consulta Reiniciada',
          `${capitalize(
            immediateAttendance?.professionalName?.trim()?.toLowerCase(),
          )} está precisando entrar em contato com você.`,
          immediateAttendance?.professionalPhoto,
        )

        if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
          const attendanceConfig: IAttendanceConfig = {
            attendanceId:
              immediateAttendance?.call?.attendanceId ?? attendanceId,
            modalToRender: 'CALL_RESTARTED',
            modalTitle: 'Consulta Reiniciada',
          }
          handleAttendanceStartWithToken(attendanceConfig)
        } else {
          setTimeout(() => {
            showModal('CALL_RESTARTED', {
              title: 'Consulta Reiniciada',
              persistent: true,
            })
          }, 500)
        }

        startTimer()
        break
      case ATTENDANCE_STATES.CALL_ENDED:
        resetAttendance()
        closeModal()

        if (immediateAttendance?.call) {
          if (withoutInteractionAttendance?.value) {
            clinic?.linkRedirectAfterAttendance
              ? (window.location.href = clinic?.linkRedirectAfterAttendance)
              : navigate('/')
            return
          }

          if (clinic?.flag?.DesabilitarNpsTelemedicina) {
            embedOrEmbedFull ? navigate('/meus-atendimentos') : navigate('/')
            return
          }

          if (immediateAttendance?.call?.attendanceId ?? attendanceId) {
            navigate(
              `/avaliacao/${
                immediateAttendance?.call?.attendanceId ?? attendanceId
              }`,
              {
                consultationType: 'immediate',
              },
            )
          }
        } else {
          if (waitingRoomMatch?.isExact) {
            showModal('CANCELLED_BY_MEDIC', {
              title: 'Consulta cancelada',
              lockable: true,
              persistent: true,
              width: 512,
            })
          }
        }
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate?.status])

  const endScheduleDateIsBeforeCurrentDate = useCallback(() => {
    if (nextScheduled?.endScheduledDate) {
      const currentDate = DateTime.now()
      const endScheduleDate = DateTime.fromFormat(
        nextScheduled?.endScheduledDate,
        'dd/MM/yyyy HH:mm:ss',
      )

      return currentDate.toJSDate() <= endScheduleDate.toJSDate()
    }
  }, [nextScheduled?.endScheduledDate])

  useEffect(() => {
    if (!canRedirect()) return

    if (
      !END_CONSULTATION_STATES.includes(nextScheduled?.status) &&
      nextScheduled?.scheduledStatus === SCHEDULING_STATES.ATTENDANCE_LATE &&
      endScheduleDateIsBeforeCurrentDate() &&
      !callStartedOptions?.includes(nextScheduled?.status)
    ) {
      showModalValidating2FA('ATTENDANCE_LATE', {
        title: 'Aguarde',
      })
      notifyValidating2FA('Conexa Saúde', `Sua consulta esta quase começando.`)

      return
    }

    if (
      !END_CONSULTATION_STATES.includes(nextScheduled?.status) &&
      nextScheduled?.scheduledStatus === SCHEDULING_STATES.RESCHEDULED
    ) {
      showModalValidating2FA('RESCHEDULED_BY_MEDIC', {
        title: 'Consulta reagendada',
        width: 512,
        persistent: true,
      })

      return
    }

    const isScheduledDateToday =
      nextScheduled?.scheduledDate?.split(' ')[0] ===
      DateTime.now().toFormat('dd/MM/yyyy')

    switch (nextScheduled?.status) {
      case ATTENDANCE_STATES.ATTENDANCE_STARTED:
        if (
          !waitingRoomMatch?.isExact &&
          ![
            SCHEDULING_STATES.ATTENDANCE_LATE,
            SCHEDULING_STATES.RESCHEDULED,
          ].includes(nextScheduled?.scheduledStatus) &&
          isScheduledDateToday
        ) {
          showModalValidating2FA('ATTENDANCE_STARTED', {
            title: 'Falta pouco!',
          })
        }
        break
      case ATTENDANCE_STATES.ATTENDANCE_END:
        resetAttendance()
        closeModal()

        if (clinic?.flag?.DesabilitarNpsTelemedicina) {
          embedOrEmbedFull ? navigate('/meus-atendimentos') : navigate('/')
          return
        }
        navigate(`/avaliacao/${nextScheduled.id}`)

        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_MEDIC:
        resetAttendance()
        clearTimer()
        if (waitingRoomMatch?.isExact) {
          showModalValidating2FA('CANCELLED_BY_MEDIC', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
            width: 512,
          })
        }
        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_TIME:
        resetAttendance()
        clearTimer()

        if (scheduledSpeciality?.canSchedule && !embedOrEmbedFull) {
          showModalValidating2FA('CANCELLED_BY_EXCEEDED_TIME_WITH_SCHEDULE', {
            title: 'Chamada cancelada',
            lockable: true,
            persistent: true,
          })
        } else {
          showModalValidating2FA('CANCELLED_BY_EXCEEDED_TIME', {
            title: 'Chamada cancelada',
            lockable: true,
            persistent: true,
          })
        }

        break
      case ATTENDANCE_STATES.CALL_STARTED:
        if (
          location.pathname === '/chamada' ||
          location?.pathname?.includes(roomPaths.basePath)
        )
          return
        notifyValidating2FA(
          'Tudo pronto para sua consulta',
          `${capitalize(
            nextScheduled?.professionalName?.trim()?.toLowerCase(),
          )} já está aguardando por você. Vamos começar?`,
          nextScheduled?.professionalPhoto,
        )

        if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
          const attendanceConfig: IAttendanceConfig = {
            attendanceId: attendanceCall?.attendanceId ?? attendanceId,
            modalToRender: 'SCHEDULED_CALL_STARTED',
            modalTitle: 'Tudo pronto para sua consulta',
            isScheduled: true,
            professionalId: attendanceCall?.professionalId,
            scheduleDateAndHour: returnFormattedDate(),
          }
          handleAttendanceStartWithToken(attendanceConfig)
        } else {
          setTimeout(() => {
            showModalValidating2FA('SCHEDULED_CALL_STARTED', {
              title: 'Está na hora da sua consulta',
              lockable: true,
              persistent: true,
              width: 512,
            })
          }, 500)
        }

        startTimer()
        break
      case ATTENDANCE_STATES.CALL_RESTARTED:
        if (
          location.pathname === '/chamada' ||
          location?.pathname?.includes(roomPaths.basePath)
        )
          return

        notifyValidating2FA(
          'Consulta Reiniciada',
          `${capitalize(
            nextScheduled?.professionalName?.trim()?.toLowerCase(),
          )} está precisando entrar em contato com você.`,
          nextScheduled?.professionalPhoto,
        )
        if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
          const attendanceConfig: IAttendanceConfig = {
            attendanceId: attendanceCall?.attendanceId ?? attendanceId,
            modalToRender: 'CALL_RESTARTED',
            modalTitle: 'Consulta Reiniciada',
            isScheduled: true,
            professionalId: attendanceCall?.professionalId,
            scheduleDateAndHour: returnFormattedDate(),
          }
          handleAttendanceStartWithToken(attendanceConfig)
        } else {
          setTimeout(() => {
            showModalValidating2FA('CALL_RESTARTED', {
              title: 'Consulta Reiniciada',
              persistent: true,
              width: 512,
            })
          }, 500)
        }
        startTimer()
        break
      case ATTENDANCE_STATES.CALL_ENDED:
        destroyMeet()
        resetAttendance()
        closeModal()

        if (attendanceCall) {
          if (clinic?.flag?.DesabilitarNpsTelemedicina) {
            embedOrEmbedFull ? navigate('/meus-atendimentos') : navigate('/')
            return
          }

          setTimeout(() => {
            navigate(
              `/avaliacao/${attendanceCall?.attendanceId ?? attendanceId}`,
            )
          }, 300)
        } else {
          if (waitingRoomMatch?.isExact) {
            showModal('CANCELLED_BY_MEDIC', {
              title: 'Consulta cancelada',
              lockable: true,
              persistent: true,
              width: 512,
            })
          }
        }
        break
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nextScheduled?.status,
    nextScheduled?.scheduledStatus,
    attendanceCall,
    endScheduleDateIsBeforeCurrentDate,
  ])
}

export default useRealtimeAttendance
